import React, { useMemo } from "react";
import { Icon, Stack, Text } from "@bookingcom/bui-react";
import { useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { productIcons } from "@flights/branded-fares";
import FlightDetailsAncillaryItemLayout from "../../../../components/elements/FlightDetailsAncillaryItemLayout/index.js";
import useFormatPrice from "../../../../hooks/useFormatPrice.js";
import { LoadingAncillariesPrice } from "./LoadingAncillariesPrice.js";
import styles from "./BaggageItem.module.css";
import { formattingUtils } from "@flights/web-api-utils-universal";
import { FlightCardBadge } from "../../../../components/elements/FlightCard/components/FlightCardBadge.js";
import { getCampaignBadgesConfig } from "../../../../utils/flightCampaignConfig.js";
import { useSelector } from "react-redux";
import { getPreOptedAncillaries } from "../../../store/extraProducts/selectors.js";
import { useRyanAirAncillaryPriceLabel } from "../../../../hooks/useRyanAirAncillaryPriceLabel.js";
export const BaggageItem = ({ type, product, hideIncludedSection, showLoadingPrice, renderTextOnly, dataTestId, hasGeniusDeal }) => {
    const i18n = useI18n();
    const { formatPrice, startingPrice } = useFormatPrice();
    const preOptedAncillaries = useSelector(getPreOptedAncillaries);
    const preOptedCheckedBaggage = preOptedAncillaries?.checkedInBaggage;
    const { priceBreakdown, luggageAllowance } = product;
    const geniusDealAncillaryBadges = "badges" in product
        ? getCampaignBadgesConfig(product?.badges).filter((badge) => badge.style !== "GENIUS")
        : undefined;
    const price = priceBreakdown ? startingPrice(priceBreakdown.total) : "";
    const priceStrikethrough = priceBreakdown?.showPriceStrikethrough && priceBreakdown?.totalWithoutDiscount
        ? formatPrice(priceBreakdown.totalWithoutDiscount)
        : undefined;
    const priceStrikethroughA11yText = !!priceStrikethrough
        ? i18n.trans(t("a11y_flights_fd_genius_checked_bag_baggage_available_from_price", {
            variables: {
                price: formatPrice(priceBreakdown.total),
                old_price: priceStrikethrough
            }
        }))
        : undefined;
    const IconSVG = productIcons[type];
    const hasIncludedBaggage = Boolean(type === "checkedInBaggage" && preOptedCheckedBaggage);
    const pieces = hasIncludedBaggage ? luggageAllowance.maxPiece : undefined;
    const weightCopy = hasIncludedBaggage
        ? formattingUtils.luggageAllowanceCopyV2(luggageAllowance, i18n).weightPerPiece
        : undefined;
    const { getRyanAirPriceLabel } = useRyanAirAncillaryPriceLabel();
    const ryanAirPriceLabel = getRyanAirPriceLabel(priceBreakdown);
    const statusBasedPriceDisplay = useMemo(() => {
        if (!showLoadingPrice) {
            return (React.createElement(Stack, { direction: "row", alignItems: "center", attributes: !!priceStrikethroughA11yText ? { "aria-label": priceStrikethroughA11yText } : {} },
                React.createElement(Text, { attributes: { "data-testid": `${dataTestId}_price`, "aria-hidden": !!priceStrikethroughA11yText }, variant: "body_2" }, price),
                priceStrikethrough && (React.createElement(Text, { attributes: { "aria-hidden": !!priceStrikethroughA11yText }, variant: "body_2", className: styles.priceStrikethrough }, priceStrikethrough))));
        }
        return (React.createElement(LoadingAncillariesPrice, { price: price, productTitle: getBaggageProductTitle(type, i18n, pieces), type: type }));
    }, [price, type, pieces, priceStrikethrough, priceStrikethroughA11yText, i18n, showLoadingPrice, dataTestId]);
    const titleAndBadgesContent = (React.createElement(Stack, { direction: "row" },
        React.createElement(Text, { attributes: { "data-testid": `${dataTestId}_title` }, variant: "body_2" }, getBaggageProductTitle(type, i18n, pieces)),
        hasGeniusDeal && geniusDealAncillaryBadges && geniusDealAncillaryBadges.length > 0 && (React.createElement("div", { className: styles.badges }, geniusDealAncillaryBadges?.map((badge, idx) => (React.createElement(FlightCardBadge, { key: `${badge.style}-${badge.text || idx}`, ...badge })))))));
    let titleAndPrice;
    if (renderTextOnly) {
        const isEach = luggageAllowance.maxPiece > 1;
        const isEntireTrip = !!(luggageAllowance.piecePerPax && luggageAllowance.piecePerPax !== luggageAllowance.maxPiece);
        const isCabin = type === "cabinBaggagePerTraveller";
        const bagPrice = formatPrice(priceBreakdown.total);
        const variables = { variables: { bag_price: bagPrice } };
        if (isCabin && !isEach && !isEntireTrip) {
            titleAndPrice = i18n.trans(t("flights_fd_baggage_cabin_from", variables));
        }
        else if (isCabin && isEach && !isEntireTrip) {
            titleAndPrice = i18n.trans(t("flights_fd_baggage_cabin_from_each", variables));
        }
        else if (isCabin && !isEach && isEntireTrip) {
            titleAndPrice = i18n.trans(t("flights_fd_baggage_cabin_entire", variables));
        }
        else if (isCabin && isEach && isEntireTrip) {
            titleAndPrice = i18n.trans(t("flights_fd_baggage_cabin_each_entire", variables));
        }
        else if (!isCabin && !isEach && !isEntireTrip) {
            titleAndPrice = i18n.trans(t("flights_fd_baggage_checked_from", variables));
        }
        else if (!isCabin && isEach && !isEntireTrip) {
            titleAndPrice = i18n.trans(t("flights_fd_baggage_checked_from_each", variables));
        }
        else if (!isCabin && !isEach && isEntireTrip) {
            titleAndPrice = i18n.trans(t("flights_fd_baggage_checked_entire", variables));
        }
        else if (!isCabin && isEach && isEntireTrip) {
            titleAndPrice = i18n.trans(t("flights_fd_baggage_checked_each_entire", variables));
        }
    }
    return titleAndPrice ? (React.createElement(Stack, { gap: 0 },
        titleAndPrice ? (React.createElement(Text, { attributes: { "data-testid": `${dataTestId}_title` } },
            titleAndPrice,
            hasGeniusDeal &&
                geniusDealAncillaryBadges &&
                geniusDealAncillaryBadges.length > 0 &&
                geniusDealAncillaryBadges?.map((badge, idx) => (React.createElement(React.Fragment, null,
                    " ",
                    React.createElement(FlightCardBadge, { key: `${badge.style}-${badge.text || idx}`, ...badge })))))) : undefined,
        weightCopy ? (React.createElement(Text, { attributes: { "data-testid": `${dataTestId}_price` }, variant: "body_2" }, weightCopy)) : undefined,
        ryanAirPriceLabel ? (React.createElement(Text, { tagName: "span", attributes: { "data-testid": `${dataTestId}_ryanair_price_breakdown` }, color: "neutral_alt" }, ryanAirPriceLabel)) : undefined)) : (React.createElement(FlightDetailsAncillaryItemLayout, { renderTextOnly: renderTextOnly, icon: React.createElement(Icon, { svg: IconSVG, size: "large" }), content: React.createElement("div", { "aria-live": "polite", "aria-relevant": "removals" },
            titleAndBadgesContent,
            weightCopy ? (React.createElement(Text, { attributes: { "data-testid": `${dataTestId}_price` }, variant: "body_2" }, weightCopy)) : (statusBasedPriceDisplay),
            ryanAirPriceLabel ? (React.createElement(Text, { attributes: { "data-testid": `${dataTestId}_ryanair_price_breakdown` }, color: "neutral_alt" }, ryanAirPriceLabel)) : undefined), includedBaggage: hasIncludedBaggage, hideIncludedSection: hideIncludedSection }));
};
const getBaggageProductTitle = (type, i18n, maxPiece) => {
    switch (type) {
        case "cabinBaggagePerTraveller":
            return maxPiece
                ? i18n.trans(t("flights_baggage_details_count_cabin", {
                    variables: { num_exception: maxPiece, num_bags: maxPiece }
                }))
                : i18n.trans(t("flights_ancillary_baggage_cabin"));
        case "checkedInBaggage":
            return maxPiece
                ? i18n.trans(t("flights_baggage_details_count_checked", {
                    variables: { num_exception: maxPiece, num_bags: maxPiece }
                }))
                : i18n.trans(t("flights_ancillary_baggage_checked"));
        default:
            throw Error(`Unknown baggage type: ${type}`);
    }
};
